import React, { useState } from 'react'
import { AlertCircle, Clock, ArrowRight } from 'lucide-react'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import { formatDateFull } from '@/lib/dateFormat'
import { KpiDrilldownModal } from './KpiDrilldownModal'
import type {
  OperationalDashboardData,
  KpiGroupKey,
} from '@/services/reports/inventoryDashboardApi'

export interface OperationalDashboardTabProps {
  data: OperationalDashboardData
  loading?: boolean
}

interface KpiCardConfig {
  key: KpiGroupKey
  label: string
  value: number | string
  hint: string
  tone?: 'default' | 'warning' | 'danger' | 'success'
  badge?: string
}

export function OperationalDashboardTab({
  data,
  loading,
}: OperationalDashboardTabProps): React.ReactElement {
  const [modalKpi, setModalKpi] = useState<{
    key: KpiGroupKey
    title: string
  } | null>(null)

  const kpiConfigs: KpiCardConfig[] = [
    {
      key: 'all',
      label: 'Tổng số mã vật tư',
      value: formatVnNumber(data.totalItems),
      hint: 'Danh mục vật tư trong kho',
      tone: 'default',
    },
    {
      key: 'physical_stock',
      label: 'Tổng tồn kho thực tế',
      value: formatVnNumber(data.totalPhysicalStock),
      hint: 'Số lượng vật lý hiện hữu',
      tone: 'default',
    },
    {
      key: 'available_stock',
      label: 'Tồn khả dụng',
      value: formatVnNumber(data.totalAvailableStock),
      hint: 'Tồn sẵn sàng cấp phát / xuất',
      tone: 'success',
    },
    {
      key: 'active_hold',
      label: 'Tổng lượng đang giữ hàng',
      value: formatVnNumber(data.activeHoldStock),
      hint: 'Đang giữ cho các LSX / đơn hàng',
      tone: 'warning',
    },
    {
      key: 'out_of_stock',
      label: 'Hàng sắp hết / Hết hàng',
      value: formatVnNumber(data.outOfStockCount),
      hint: 'Tồn thực tế = 0 cần bổ sung',
      tone: data.outOfStockCount > 0 ? 'danger' : 'default',
      badge: data.outOfStockCount > 0 ? 'Khẩn cấp' : undefined,
    },
    {
      key: 'below_min',
      label: 'Hàng dưới định mức tối thiểu',
      value: formatVnNumber(data.belowMinCount),
      hint: 'Tồn khả dụng ≤ mức tối thiểu',
      tone: data.belowMinCount > 0 ? 'warning' : 'default',
      badge: data.belowMinCount > 0 ? 'Cảnh báo' : undefined,
    },
    {
      key: 'slow_moving',
      label: 'Hàng tồn lâu không phát sinh',
      value: formatVnNumber(data.slowMovingCount),
      hint: '> 30 ngày không có nhập / xuất',
      tone: 'default',
    },
    {
      key: 'unpriced',
      label: 'Hàng chưa xác định giá vốn',
      value: formatVnNumber(data.unpricedStockCount),
      hint: 'Chưa cập nhật đơn giá mua / định mức',
      tone: 'default',
    },
  ]

  return (
    <div className="space-y-6">
      {/* KPI Cards Grid */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium text-[var(--color-text-muted)]">
            Nhấp chuột vào từng thẻ để xem danh sách chi tiết vật tư
          </span>
          {loading && (
            <span className="text-xs text-[var(--color-text-muted)] italic">Đang tải dữ liệu...</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4">
          {kpiConfigs.map((kpi) => {
            const isDanger = kpi.tone === 'danger'
            const isWarning = kpi.tone === 'warning'
            const isSuccess = kpi.tone === 'success'

            const borderColor = isDanger
              ? 'border-red-300 hover:border-red-400 bg-red-50/20'
              : isWarning
              ? 'border-amber-300 hover:border-amber-400 bg-amber-50/20'
              : isSuccess
              ? 'border-emerald-300 hover:border-emerald-400'
              : 'border-[var(--color-border)] hover:border-slate-400'

            const valueColor = isDanger
              ? 'text-red-600'
              : isWarning
              ? 'text-amber-700'
              : isSuccess
              ? 'text-emerald-700'
              : 'text-[var(--color-text)]'

            return (
              <button
                key={kpi.key}
                type="button"
                data-testid={`kpi-card-${kpi.key}`}
                onClick={() => setModalKpi({ key: kpi.key, title: `Chi tiết: ${kpi.label}` })}
                className={`group flex flex-col justify-between rounded-[var(--radius-lg)] border bg-[var(--color-surface)] p-3.5 text-left shadow-[var(--shadow-level-1)] transition-all hover:shadow-md cursor-pointer ${borderColor}`}
              >
                <div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] line-clamp-1">
                      {kpi.label}
                    </span>
                    {kpi.badge && (
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          isDanger ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {kpi.badge}
                      </span>
                    )}
                  </div>
                  <div className={`mt-2 text-xl sm:text-2xl font-bold tabular-nums tracking-tight break-normal ${valueColor}`}>
                    {kpi.value}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-[var(--color-text-muted)]">
                  <span className="line-clamp-1">{kpi.hint}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 text-[var(--color-text-muted)]" />
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Cảnh báo: Giữ hàng từ LSX đã hoàn thành */}
      <div
        data-testid="completed-lsx-reservations-alert"
        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-[var(--radius-lg)] border p-4 shadow-[var(--shadow-level-1)] ${
          (data.completedLsxReservationsCount ?? 0) > 0
            ? 'border-amber-300 bg-amber-50/60'
            : 'border-[var(--color-border)] bg-[var(--color-surface)]'
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              (data.completedLsxReservationsCount ?? 0) > 0
                ? 'bg-amber-100 text-amber-700'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            <AlertCircle className="h-5 w-5" />
          </div>
          <div>
            <h3
              className={`text-base font-semibold ${
                (data.completedLsxReservationsCount ?? 0) > 0 ? 'text-amber-900' : 'text-slate-900'
              }`}
            >
              {`Giữ hàng từ LSX đã hoàn thành: ${data.completedLsxReservationsCount ?? 0} dòng`}
            </h3>
            <p className="text-xs text-[var(--color-text-muted)]">
              {(data.completedLsxReservationsCount ?? 0) > 0
                ? 'Lệnh sản xuất đã xong nhưng vật tư giữ chưa được giải phóng. Thủ kho và mua hàng tự bấm bỏ giữ bằng tay.'
                : 'Không có dòng giữ hàng nào từ lệnh sản xuất đã hoàn thành cần xử lý.'}
            </p>
          </div>
        </div>
        <a
          href="/warehouse/reservations?status=ACTIVE&completed_lsx=1"
          className={`inline-flex shrink-0 self-start sm:self-auto items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold transition-colors ${
            (data.completedLsxReservationsCount ?? 0) > 0
              ? 'bg-amber-600 text-white hover:bg-amber-700'
              : 'border border-[var(--color-border)] bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <span>Xem danh sách giữ hàng</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* Warning Section 1: Urgent Items */}
      <div className="rounded-[var(--radius-lg)] border border-red-200 bg-white p-4 shadow-[var(--shadow-level-1)]">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600">
              <AlertCircle className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Việc cần xử lý ngay ({data.urgentItems.length})
              </h3>
              <p className="text-xs text-[var(--color-text-muted)]">
                Vật tư hết hàng hoặc dưới định mức tối thiểu đang có lệnh giữ hoặc nhu cầu cấp phát
              </p>
            </div>
          </div>
          {data.urgentItems.length > 0 && (
            <span className="rounded-full bg-red-50 border border-red-200 px-2.5 py-0.5 text-xs font-semibold text-red-700">
              Ưu tiên cao
            </span>
          )}
        </div>

        {data.urgentItems.length === 0 ? (
          <div className="sticky left-0 py-8 text-center text-sm text-[var(--color-text-muted)]">
            Không có vật tư nào hết hàng hoặc cần xử lý khẩn cấp.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-[var(--color-border)]">
            <table className="min-w-full divide-y divide-[var(--color-border)] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                <tr>
                  <th scope="col" className="px-3 py-2.5 text-center w-12">
                    STT
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Mã vật tư
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Tên vật tư
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Nhóm
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-center w-16">
                    ĐVT
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-right">
                    Tồn thực tế
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-right">
                    Khả dụng
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-right">
                    Đang giữ
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-right">
                    Định mức
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Trạng thái & Hướng xử lý
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)] bg-white">
                {data.urgentItems.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-slate-50/80 ${
                      item.status_tone === 'danger' ? 'bg-red-50/30' : 'bg-amber-50/20'
                    }`}
                  >
                    <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-800 whitespace-nowrap">
                      {item.material_code}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900 break-words leading-snug" title={item.material_name}>
                      {item.material_name}
                    </td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">
                      {item.category_name}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-600">{item.uom}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-red-600">
                      {formatVnNumber(item.physical_stock)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums text-red-600">
                      {formatVnNumber(item.available_stock)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700 font-semibold">
                      {formatVnNumber(item.reserved_stock)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-600">
                      {formatVnNumber(item.min_stock)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        <StatusBadge
                          tone={item.status_tone === 'danger' ? 'danger' : 'warning'}
                          label={item.status_label}
                        />
                        <span className="text-[11px] text-[var(--color-text-muted)] italic">
                          {item.action_hint}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Warning Section 2: Slow Moving Items */}
      <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-4 shadow-[var(--shadow-level-1)]">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600">
              <Clock className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Hàng tồn lâu không phát sinh giao dịch &gt; 30 ngày ({data.slowMovingItems.length})
              </h3>
              <p className="text-xs text-[var(--color-text-muted)]">
                Vật tư không có giao dịch nhập hoặc xuất trong vòng 30 ngày qua, cần xem xét giải phóng hoặc thanh lý
              </p>
            </div>
          </div>
          {data.slowMovingItems.length > 0 && (
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
              Cần rà soát
            </span>
          )}
        </div>

        {data.slowMovingItems.length === 0 ? (
          <div className="sticky left-0 py-8 text-center text-sm text-[var(--color-text-muted)]">
            Không có hàng tồn lâu vượt 30 ngày.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-[var(--color-border)]">
            <table className="min-w-full divide-y divide-[var(--color-border)] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                <tr>
                  <th scope="col" className="px-3 py-2.5 text-center w-12">
                    STT
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Mã vật tư
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Tên vật tư
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Nhóm
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-center w-16">
                    ĐVT
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-right">
                    Tồn kho
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-center">
                    Giao dịch cuối
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-right">
                    Số ngày bất động
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-right">
                    Giá trị ước tính
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)] bg-white">
                {data.slowMovingItems.map((item, index) => (
                  <tr key={item.id} className="hover:bg-slate-50/80">
                    <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-800 whitespace-nowrap">
                      {item.material_code}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900 break-words leading-snug" title={item.material_name}>
                      {item.material_name}
                    </td>
                    <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">
                      {item.category_name}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-600">{item.uom}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-900">
                      {formatVnNumber(item.physical_stock)}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-600">
                      {formatDateFull(item.last_movement_date)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-amber-700">
                      {item.days_inactive} ngày
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-700 whitespace-nowrap">
                      {item.estimated_value != null && item.estimated_value > 0 ? (
                        `${formatVnNumber(item.estimated_value)} ₫`
                      ) : (
                        <span className="text-slate-400 italic text-[11px]">Chưa có giá</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drilldown Modal */}
      {modalKpi && (
        <KpiDrilldownModal
          isOpen={true}
          onClose={() => setModalKpi(null)}
          title={modalKpi.title}
          items={data.drilldownItems}
          kpiKey={modalKpi.key}
        />
      )}
    </div>
  )
}
